import { useState, useEffect } from "react";
import { useUser } from "../context/UserContext";
import { clientsEndpoint } from "../constants";

const PerformanceDashboard = () => {
  const { user } = useUser();
  const [feedbackPeriod, setFeedbackPeriod] = useState("12months");
  const [hirePeriod, setHirePeriod] = useState("12months");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showFilterModal, setShowFilterModal] = useState(false);

  const [filters, setFilters] = useState({
    timeRange: "All time",
    locations: ["US", "AU"],
    status: [],
  });

  const [availableFilters] = useState({
    timeRanges: ["All time", "Last 7 days", "Last 30 days", "Last 3 months", "Last year"],
    locations: ["US", "AU", "UK", "CA", "DE", "FR", "JP", "IN"],
    statuses: ["High potential", "At Risk", "Active", "Inactive"],
  });

  const [dashboardData, setDashboardData] = useState({
    metrics: {
      avgFeedbackTurnaround: { value: "2,420", change: 40, trend: "up" },
      candidatesPerHire: { value: "1,210", change: 10, trend: "down" },
      interviewToOffer: { value: "2 Weeks", change: 20, trend: "up" },
      totalRevenue: { value: "$4,200,000", change: 20, trend: "up" },
    },
    feedbackChartData: [],
    hireChartData: [],
    clients: [
      { id: 1, name: "Morph Mind", avatar: null, openPosition: 215, hired: 100, tasksCreated: "03", status: "High potential", lastUpdated: "May 22, 2025 1:50 PM" },
      { id: 2, name: "Morph Mind", avatar: null, openPosition: 42, hired: 28, tasksCreated: "12", status: "High potential", lastUpdated: "May 21, 2025 3:20 PM" },
      { id: 3, name: "Morph", avatar: null, openPosition: 87, hired: 45, tasksCreated: "08", status: "At Risk", lastUpdated: "May 20, 2025 10:15 AM" },
      { id: 4, name: "Mind", avatar: null, openPosition: 156, hired: 72, tasksCreated: "15", status: "High potential", lastUpdated: "May 19, 2025 4:45 PM" },
      { id: 5, name: "NextGen Labs", avatar: null, openPosition: 37, hired: 19, tasksCreated: "06", status: "At Risk", lastUpdated: "May 18, 2025 9:30 AM" },
      { id: 6, name: "Innovate Hub", avatar: null, openPosition: 98, hired: 56, tasksCreated: "11", status: "High potential", lastUpdated: "May 17, 2025 2:00 PM" },
      { id: 7, name: "Morph Mind", avatar: null, openPosition: 25, hired: 83, tasksCreated: "09", status: "High potential", lastUpdated: "May 16, 2025 11:25 AM" },
    ],
    totalClients: 70,
  });

  const fetchMetrics = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${clientsEndpoint}dashboard/metrics/`, {
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (response.ok) {
        const data = await response.json();
        setDashboardData((prev) => ({ ...prev, metrics: data }));
      }
    } catch (error) {
      console.error("Error fetching metrics:", error);
    }
  };

  const fetchFeedbackChart = async (period) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${clientsEndpoint}dashboard/feedback-chart/?period=${period}`, {
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (response.ok) {
        const data = await response.json();
        setDashboardData((prev) => ({ ...prev, feedbackChartData: data }));
      }
    } catch (error) {
      console.error("Error fetching feedback chart:", error);
    }
  };

  const fetchHireChart = async (period) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${clientsEndpoint}dashboard/hire-chart/?period=${period}`, {
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (response.ok) {
        const data = await response.json();
        setDashboardData((prev) => ({ ...prev, hireChartData: data }));
      }
    } catch (error) {
      console.error("Error fetching hire chart:", error);
    }
  };

  const fetchClients = async (page, search, filterParams) => {
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({
        page: page.toString(),
        search: search,
        time_range: filterParams.timeRange,
        locations: filterParams.locations.join(","),
        status: filterParams.status.join(","),
      });
      const response = await fetch(`${clientsEndpoint}dashboard/clients/?${params}`, {
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (response.ok) {
        const data = await response.json();
        setDashboardData((prev) => ({
          ...prev,
          clients: data.results || data,
          totalClients: data.count || data.length,
        }));
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  useEffect(() => {
    const loadDashboard = async () => {
      setLoading(true);
      await Promise.all([
        fetchMetrics(),
        fetchFeedbackChart(feedbackPeriod),
        fetchHireChart(hirePeriod),
        fetchClients(currentPage, searchQuery, filters),
      ]);
      setLoading(false);
    };
    loadDashboard();
  }, []);

  useEffect(() => {
    fetchFeedbackChart(feedbackPeriod);
  }, [feedbackPeriod]);

  useEffect(() => {
    fetchHireChart(hirePeriod);
  }, [hirePeriod]);

  useEffect(() => {
    fetchClients(currentPage, searchQuery, filters);
  }, [currentPage, searchQuery, filters]);

  const getActiveFilters = () => {
    const active = [];
    if (filters.timeRange && filters.timeRange !== "All time") {
      active.push({ key: "timeRange", label: filters.timeRange });
    } else if (filters.timeRange === "All time") {
      active.push({ key: "timeRange", label: "All time" });
    }
    if (filters.locations.length > 0) {
      const label = filters.locations.length > 2
        ? `${filters.locations.slice(0, 2).join(", ")}, +${filters.locations.length - 2}`
        : filters.locations.join(", ");
      active.push({ key: "locations", label });
    }
    filters.status.forEach((s) => {
      active.push({ key: `status_${s}`, label: s, type: "status", value: s });
    });
    return active;
  };

  const removeFilter = (filterItem) => {
    if (filterItem.key === "timeRange") {
      setFilters((prev) => ({ ...prev, timeRange: "" }));
    } else if (filterItem.key === "locations") {
      setFilters((prev) => ({ ...prev, locations: [] }));
    } else if (filterItem.type === "status") {
      setFilters((prev) => ({
        ...prev,
        status: prev.status.filter((s) => s !== filterItem.value),
      }));
    }
  };

  const toggleLocation = (loc) => {
    setFilters((prev) => ({
      ...prev,
      locations: prev.locations.includes(loc)
        ? prev.locations.filter((l) => l !== loc)
        : [...prev.locations, loc],
    }));
  };

  const toggleStatus = (status) => {
    setFilters((prev) => ({
      ...prev,
      status: prev.status.includes(status)
        ? prev.status.filter((s) => s !== status)
        : [...prev.status, status],
    }));
  };

  const totalPages = Math.ceil(dashboardData.totalClients / 10);

  const renderMiniChart = (color) => {
    const path = color === "red"
      ? "M0 30L20 25L40 35L60 30L80 40L100 35"
      : color === "orange"
      ? "M0 35L20 30L40 40L60 35L80 45L100 40"
      : "M0 45L20 35L40 40L60 30L80 35L100 25";
    return (
      <svg className="w-full h-full" viewBox="0 0 100 60" fill="none">
        <path
          d={path}
          stroke={color === "red" ? "#ef4444" : color === "orange" ? "#f97316" : "#19bfb7"}
          strokeWidth="1.5"
          fill="none"
        />
      </svg>
    );
  };

  const renderChart = (type) => {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const yAxisLabel = type === "feedback" ? "Feedbacks" : "Hire count";

    const tealLine = type === "feedback"
      ? [25, 28, 26, 30, 32, 35, 38, 42, 40, 45, 48, 50]
      : [18, 22, 20, 25, 28, 30, 32, 35, 38, 42, 45, 50];

    const grayLine = type === "feedback"
      ? [12, 15, 18, 20, 22, 25, 28, 30, 32, 35, 38, 40]
      : [10, 12, 15, 18, 20, 22, 25, 28, 30, 32, 35, 38];

    const getPoints = (data) => data.map((y, i) => `${i * 36},${160 - y * 3}`).join(" ");

    return (
      <div className="relative h-52 flex">
        <div className="flex items-center pr-1">
          <span className="text-xs text-gray-500 whitespace-nowrap -rotate-90 origin-center">{yAxisLabel}</span>
        </div>
        <div className="flex flex-col justify-between text-xs text-gray-400 pr-2 py-1">
          <span>50</span>
          <span>40</span>
          <span>30</span>
          <span>20</span>
          <span>10</span>
          <span>0</span>
        </div>
        <div className="flex-1 flex flex-col">
          <svg className="w-full flex-1" viewBox="0 0 400 160" preserveAspectRatio="none">
            <polyline fill="none" stroke="#19bfb7" strokeWidth="2" points={getPoints(tealLine)} />
            {tealLine.map((y, i) => (
              <circle key={`t${i}`} cx={i * 36} cy={160 - y * 3} r="3" fill="#19bfb7" />
            ))}

            <polyline fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeDasharray="4,2" points={getPoints(grayLine)} />
            {grayLine.map((y, i) => (
              <circle key={`g${i}`} cx={i * 36} cy={160 - y * 3} r="2.5" fill="#9ca3af" />
            ))}
          </svg>
          <div className="flex justify-between text-xs text-gray-400 mt-2">
            {months.map((m) => (
              <span key={m}>{m}</span>
            ))}
          </div>
          <div className="text-center mt-1">
            <span className="text-xs text-gray-500">Month</span>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#19bfb7]"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Welcome back, {user ? user.first_name || user.username : 'Guest'}
          </h1>
          <p className="text-sm text-gray-500">
            Here is a complete AI overview of your hiring.
          </p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
          Import
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-lg">
          <p className="text-sm text-gray-500 mb-2">Avg Feedback Turnaround</p>
          <p className="text-2xl font-semibold text-gray-900 mb-1">{dashboardData.metrics.avgFeedbackTurnaround.value}</p>
          <p className={`text-xs mb-4 ${dashboardData.metrics.avgFeedbackTurnaround.change >= 0 ? "text-green-600" : "text-red-600"}`}>
            {dashboardData.metrics.avgFeedbackTurnaround.change >= 0 ? "↑" : "↓"} {Math.abs(dashboardData.metrics.avgFeedbackTurnaround.change)}% vs last month
          </p>
          <div className="h-16">
            {renderMiniChart("teal")}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-lg">
          <p className="text-sm text-gray-500 mb-2">Candidates per Hire.</p>
          <p className="text-2xl font-semibold text-gray-900 mb-1">{dashboardData.metrics.candidatesPerHire.value}</p>
          <p className={`text-xs mb-4 ${dashboardData.metrics.candidatesPerHire.change >= 0 ? "text-green-600" : "text-red-600"}`}>
            {dashboardData.metrics.candidatesPerHire.change >= 0 ? "↑" : "↓"} {Math.abs(dashboardData.metrics.candidatesPerHire.change)}% vs last month
          </p>
          <div className="h-16">
            {renderMiniChart("red")}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-lg">
          <p className="text-sm text-gray-500 mb-2">Interview-to-Offer Ratio</p>
          <p className="text-2xl font-semibold text-gray-900 mb-1">{dashboardData.metrics.interviewToOffer.value}</p>
          <p className={`text-xs mb-4 ${dashboardData.metrics.interviewToOffer.change >= 0 ? "text-green-600" : "text-red-600"}`}>
            {dashboardData.metrics.interviewToOffer.change >= 0 ? "↑" : "↓"} {Math.abs(dashboardData.metrics.interviewToOffer.change)}% vs last month
          </p>
          <div className="h-16">
            {renderMiniChart("orange")}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-lg">
          <p className="text-sm text-gray-500 mb-2">Total Revenue & Margin</p>
          <p className="text-2xl font-semibold text-gray-900 mb-1">{dashboardData.metrics.totalRevenue.value}</p>
          <p className={`text-xs mb-4 ${dashboardData.metrics.totalRevenue.change >= 0 ? "text-green-600" : "text-red-600"}`}>
            {dashboardData.metrics.totalRevenue.change >= 0 ? "↑" : "↓"} {Math.abs(dashboardData.metrics.totalRevenue.change)}% vs last month
          </p>
          <div className="h-16">
            {renderMiniChart("teal")}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Feedback speed</h3>
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit mb-4">
            {["12months", "30days", "7days", "24hours"].map((period) => (
              <button
                key={period}
                onClick={() => setFeedbackPeriod(period)}
                className={`px-3 py-1 text-xs rounded-md transition-colors ${
                  feedbackPeriod === period ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {period === "12months" ? "12 months" : period === "30days" ? "30 days" : period === "7days" ? "7 days" : "24 hours"}
              </button>
            ))}
          </div>
          {renderChart("feedback")}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Hire count</h3>
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit mb-4">
            {["12months", "30days", "7days", "24hours"].map((period) => (
              <button
                key={period}
                onClick={() => setHirePeriod(period)}
                className={`px-3 py-1 text-xs rounded-md transition-colors ${
                  hirePeriod === period ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {period === "12months" ? "12 months" : period === "30days" ? "30 days" : period === "7days" ? "7 days" : "24 hours"}
              </button>
            ))}
          </div>
          {renderChart("hire")}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Clients</h3>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex flex-wrap items-center gap-2">
              {getActiveFilters().map((filterItem, idx) => (
                <span key={idx} className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-lg">
                  {filterItem.label}
                  <button onClick={() => removeFilter(filterItem)} className="text-gray-400 hover:text-gray-600">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              ))}
              <div className="relative">
                <button
                  onClick={() => setShowFilterModal(!showFilterModal)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                  More filters
                </button>
                {showFilterModal && (
                  <div className="absolute top-full left-0 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4">
                    <div className="mb-4">
                      <p className="text-xs font-medium text-gray-500 mb-2">Time Range</p>
                      <div className="flex flex-wrap gap-2">
                        {availableFilters.timeRanges.map((range) => (
                          <button
                            key={range}
                            onClick={() => setFilters((prev) => ({ ...prev, timeRange: range }))}
                            className={`px-2 py-1 text-xs rounded-md ${
                              filters.timeRange === range ? "bg-[#19bfb7] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                            }`}
                          >
                            {range}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="mb-4">
                      <p className="text-xs font-medium text-gray-500 mb-2">Locations</p>
                      <div className="flex flex-wrap gap-2">
                        {availableFilters.locations.map((loc) => (
                          <button
                            key={loc}
                            onClick={() => toggleLocation(loc)}
                            className={`px-2 py-1 text-xs rounded-md ${
                              filters.locations.includes(loc) ? "bg-[#19bfb7] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                            }`}
                          >
                            {loc}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="mb-4">
                      <p className="text-xs font-medium text-gray-500 mb-2">Status</p>
                      <div className="flex flex-wrap gap-2">
                        {availableFilters.statuses.map((status) => (
                          <button
                            key={status}
                            onClick={() => toggleStatus(status)}
                            className={`px-2 py-1 text-xs rounded-md ${
                              filters.status.includes(status) ? "bg-[#19bfb7] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                            }`}
                          >
                            {status}
                          </button>
                        ))}
                      </div>
                    </div>
                    <button
                      onClick={() => setShowFilterModal(false)}
                      className="w-full py-2 text-sm text-white bg-[#19bfb7] rounded-lg hover:bg-[#18ada6]"
                    >
                      Apply Filters
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#19bfb7] focus:border-transparent w-full md:w-64"
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="w-12 px-4 py-3">
                  <input type="checkbox" className="rounded border-gray-300" />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Client name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Open position</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Hired</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Tasks created</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Last updated</th>
                <th className="w-12 px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {dashboardData.clients.map((client) => (
                <tr key={client.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <input type="checkbox" className="rounded border-gray-300" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <span className="text-sm font-medium text-gray-900">{client.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{client.openPosition}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{client.hired}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{client.tasksCreated}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      client.status === "High potential" ? "bg-teal-100 text-teal-700" : "bg-red-100 text-red-700"
                    }`}>
                      {client.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{client.lastUpdated}</td>
                  <td className="px-4 py-3">
                    <button className="text-gray-400 hover:text-gray-600">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between p-4 border-t border-gray-200">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Previous
          </button>
          <div className="flex items-center gap-1">
            {[1, 2, 3].map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-8 h-8 text-sm rounded-lg ${
                  currentPage === page ? "bg-gray-100 text-gray-900 font-medium" : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                {page}
              </button>
            ))}
            {totalPages > 6 && <span className="px-2 text-gray-400">...</span>}
            {totalPages > 3 && [totalPages - 2, totalPages - 1, totalPages].filter(p => p > 3).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-8 h-8 text-sm rounded-lg ${
                  currentPage === page ? "bg-gray-100 text-gray-900 font-medium" : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                {page}
              </button>
            ))}
          </div>
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
          >
            Next
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PerformanceDashboard;